import type { ChatResumeDecision } from '@/api';
import type { HitlSubmitPayload } from '@/components/humanInTheLoop/HitlInterruptCard.vue';
import type { NormalizedInterrupt } from '@/utils/humanInTheLoop/normalizeInterrupt';

/** Thread titles are the user's first question, clipped. */
export function buildTitle(message: string): string {
  const normalized = message.trim();
  if (normalized.length <= 60) return normalized;
  return `${normalized.slice(0, 57)}...`;
}

/**
 * Append a streamed chunk to what the bubble already holds. Not a plain
 * concatenation: rejoining a run replays it from its first frame, so an
 * incoming chunk may be a superset (or a repeat) of what has already arrived.
 */
export function mergeContent(current: string, incoming: string): string {
  if (!current) return incoming;
  if (incoming.startsWith(current)) return incoming;
  if (current.startsWith(incoming)) return current;
  return current + incoming;
}

/**
 * The bubble echoed back into the transcript when the user answers an
 * interrupt — the resume frame carries the decision, but the thread needs a
 * human-readable record of what they chose.
 */
export function summarizeHitlSubmission(
  interrupt: NormalizedInterrupt,
  payload: HitlSubmitPayload
): string {
  // A question's reply is the user's answer, not a verdict on the agent's plan —
  // so the echoed bubble reads as one: the option's own label, or their own words.
  if (payload.shape === 'user_question') {
    const { resumeValue } = payload;
    if (resumeValue.choice) {
      const choice = interrupt.requests[0]?.choices.find(item => item.key === resumeValue.choice);
      return choice?.label ?? resumeValue.choice;
    }
    return resumeValue.message ?? '';
  }

  if (payload.shape === 'tool_confirmation') {
    const { resumeValue } = payload;
    if (resumeValue.choice) return `Decision: ${resumeValue.choice}`;
    return `Clarification: ${resumeValue.message ?? ''}`;
  }

  const summary = payload.decisions
    .map(
      (decision: ChatResumeDecision) =>
        decision.type + (decision.message ? ` (${decision.message})` : '')
    )
    .join(', ');
  return `Decision: ${summary}`;
}
